import {
  GAME_YEARS,
  DISASTER_DRAWS_PER_YEAR,
  EOS_DUPLICATE_MULTIPLIER
} from '../data/constants.js';
import { DEMAND_BY_YEAR } from '../data/demand.js';
import { DISASTERS } from '../data/disasters.js';
import { SLOTS_NEW_PER_YEAR, SLOTS_CUMULATIVE_BY_YEAR } from '../data/slots.js';
import {
  ADVANCED_LEGACY_PLANTS,
  ADVANCED_SLOTS_NEW_PER_YEAR,
  ADVANCED_SLOTS_CUMULATIVE_BY_YEAR
} from '../data/advancedCapacity.js';
import { CARBON_TAX_BRACKETS } from '../data/brackets.js';
import {
  CLEAN_ENERGY_AWARD_TIERS,
  RESILIENCE_AWARD_TIERS
} from '../data/awards.js';
import { FAMILY_LABEL } from '../data/families.js';
import {
  ADVANCED_PLANTS,
  ADVANCED_PLANT_BY_ID,
  ADVANCED_DEMAND_BY_YEAR,
  ADVANCED_CARBON_YEAR_WEIGHTS,
  RESOURCE_DEVELOPMENT_PREMIUM_STARTS_AT_COPY,
  RESOURCE_DEVELOPMENT_PREMIUM_PER_BUILD,
  DECOMMISSION_FEE_PER_PLANT,
  EARLY_CLOSURE_SURCHARGE,
  ADVANCED_EOS_DUPLICATE_MULTIPLIER,
  ADVANCED_EOS_FIRST_COPY_PAYS_FULL,
  RESERVE_MARGIN,
  VARIABLE_DIVERSITY_UPLIFT_PER_FAMILY,
  VARIABLE_DIVERSITY_UPLIFT_CAP,
  VARIABLE_FIRM_DECLINE_SCHEDULE,
  PEAK_SHORTFALL_TIER1_LIMIT_UNITS,
  PEAK_SHORTFALL_TIER1_RATE_PER_UNIT,
  PEAK_SHORTFALL_TIER2_RATE_PER_UNIT,
  STORAGE_PLANTS
} from '../data/advancedCapacity.js';
import { ADVANCED_DISASTER_POOL } from '../data/advancedDisasters.js';
import { PLAN_RATING_HISTORIES } from '../engine/planRating.js';
import { DisasterCard } from './DisasterCard.jsx';
import {
  decommissionFeeForPlant,
  storageSurplusToFill,
  variableFamiliesOwned
} from '../engine/advancedCapacity.js';
import { formatMoney, formatPercent, formatGw, formatTwh } from './format.js';

function joinWithAnd(items) {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

export function RulesPanel({ advanced = false }) {
  // Advanced pays for the two slots the legacy fleet occupies with a
  // fourth Year 1 slot; Classic keeps the shared table.
  const slotsNewPerYear = advanced
    ? ADVANCED_SLOTS_NEW_PER_YEAR
    : SLOTS_NEW_PER_YEAR;
  const slotsCumulativeByYear = advanced
    ? ADVANCED_SLOTS_CUMULATIVE_BY_YEAR
    : SLOTS_CUMULATIVE_BY_YEAR;

  // Derived from the plant data rather than written out, so the rules
  // cannot drift from the palette. The variable families are the ones
  // the diversity bonus actually counts — the engine's own definition.
  const variableFamilies = [
    ...variableFamiliesOwned(
      ADVANCED_PLANTS.map((p) => p.id),
      ADVANCED_PLANT_BY_ID
    )
  ]
    .map((family) => FAMILY_LABEL[family] ?? family)
    .sort();
  const variableFamilyCount = variableFamilies.length;
  const variableFamilyNames = joinWithAnd(variableFamilies);

  // The worked early-closure example uses the inherited coal, the plant
  // the charge exists to keep on the grid through Year 1.
  const coalLegacy = ADVANCED_PLANT_BY_ID['legacy-coal-plant'];

  // Naming the stores beats "a carrier" / "every other store", which
  // is indirection over a palette of exactly two.
  const carrier = STORAGE_PLANTS.find((p) => p.carriesEnergyBetweenYears);
  const carrierName = carrier?.name ?? 'the inter-year carrier';
  const carrierEfficiency = carrier?.roundTripEfficiency ?? 0;
  const nonCarrierNames =
    joinWithAnd(
      STORAGE_PLANTS.filter((p) => !p.carriesEnergyBetweenYears).map(
        (p) => p.name
      )
    ) || 'Every other store';
  const disruptionCards = advanced ? ADVANCED_DISASTER_POOL : DISASTERS;

  return (
    <div className="rules-panel" data-testid={advanced ? 'rules-advanced' : 'rules-classic'}>
      <section className="rules-section">
        <h3>The goal</h3>
        <p>
          You are an energy planner. Build a portfolio of power plants
          across {GAME_YEARS} years that meets rising electricity demand
          at the lowest possible total cost. Cost includes capital,
          operating, shortfall penalties, and carbon tax (or bonus).
          The lowest cost total wins.
        </p>
        {advanced ? (
          <p data-testid="rules-advanced-intro">
            <strong>Advanced Mode.</strong> Your grid must pass
            two separate tests every year, not one. It needs enough{' '}
            <strong>annual energy</strong> (TWh) to cover total demand,
            and enough <strong>firm capacity</strong> (GW) to cover{' '}
            <strong>peak demand</strong> — the single busiest hour of
            the year. A grid can generate plenty of energy over the year
            and still fail at peak.
          </p>
        ) : null}
      </section>

      <section className="rules-section">
        <h3>Each year</h3>
        <ol className="rules-ordered">
          <li>
            Build phase. Add plants up to the slots available this year.
            {advanced ? ' You may also retire plants you already own.' : ''}
          </li>
          <li>Variable output. One independent output outcome per variable plant type you own.</li>
          <li>System stress. Reveal the year's disruption events from the shared risk landscape.</li>
          <li>
            Resolution. Apply effects, sum output, compute shortfall and opex.
            {advanced
              ? ' Both the energy and peak capacity layers are checked separately.'
              : ''}
          </li>
        </ol>
      </section>

      <section className="rules-section">
        <h3>Demand and slots</h3>
        <table className="rules-table">
          <thead>
            <tr>
              <th>Year</th>
              <th>Demand</th>
              {advanced ? <th>Peak demand</th> : null}
              {advanced ? <th>Firm needed</th> : null}
              <th>New slots</th>
              <th>Cumulative</th>
              <th>Disasters</th>
            </tr>
          </thead>
          <tbody>
            {[1, 2, 3].map((y) => (
              <tr key={y}>
                <td>{y}</td>
                <td>
                  {advanced
                    ? `${ADVANCED_DEMAND_BY_YEAR[y].energy} TWh`
                    : `${DEMAND_BY_YEAR[y]} TWh`}
                </td>
                {advanced ? (
                  <td>{ADVANCED_DEMAND_BY_YEAR[y].peak} GW</td>
                ) : null}
                {advanced ? (
                  <td>
                    {(ADVANCED_DEMAND_BY_YEAR[y].peak * RESERVE_MARGIN).toFixed(1)} GW
                  </td>
                ) : null}
                <td>{slotsNewPerYear[y]}</td>
                <td>{slotsCumulativeByYear[y]}</td>
                <td>{DISASTER_DRAWS_PER_YEAR[y]}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {advanced ? (
          <p>
            Firm capacity required each year is peak demand multiplied by
            the {RESERVE_MARGIN}x reserve margin. Falling short costs{' '}
            {formatMoney(PEAK_SHORTFALL_TIER1_RATE_PER_UNIT)} per GW for
            the first {PEAK_SHORTFALL_TIER1_LIMIT_UNITS} GW, then{' '}
            {formatMoney(PEAK_SHORTFALL_TIER2_RATE_PER_UNIT)} per GW
            beyond that. This penalty is separate from, and additional to,
            the energy shortfall penalty.
          </p>
        ) : null}
      </section>

      {advanced ? (
        <section className="rules-section">
          <h3>Firm capacity</h3>
          <p>
            Peak demand is the single busiest hour of the year. Firm
            capacity is how much of your grid you can{' '}
            <strong>count on</strong> in that hour — which is less than
            it produces in an average one.
          </p>
          <ul className="rules-list">
            <li>
              <strong>Each technology has a fixed reliability score.</strong>{' '}
              A plant's firm capacity is its size multiplied by a score
              set by its technology. Gas and nuclear score high because
              they run on demand. Solar scores very low — the peak can
              fall after dark — and wind is not much better. The score
              never moves with the weather you roll: a windy year does
              not make wind dependable at the peak hour.
            </li>
            <li>
              <strong>Spreading across variable families raises the
              score.</strong> Variable plants depend on the weather, and
              different families do not all fail in the same hour. There
              are {variableFamilyCount}: {variableFamilyNames}. Each
              family beyond the first adds{' '}
              {VARIABLE_DIVERSITY_UPLIFT_PER_FAMILY * 100} points to the
              firm-capacity score of every variable plant you own. The
              bonus is capped at {VARIABLE_DIVERSITY_UPLIFT_CAP * 100}{' '}
              points, reached at {variableFamilyCount} families.{' '}
              <strong>It counts families, not plants</strong> — Onshore
              plus Offshore Wind is one family and earns nothing, but
              Onshore Wind plus Tidal is two and earns the bonus.
            </li>
            <li>
              <strong>Extra plants in one variable family count for
              less at peak.</strong> Within a family, the unit with the
              highest starting firm capacity receives{' '}
              {formatPercent(VARIABLE_FIRM_DECLINE_SCHEDULE[0])} credit,
              the next receives{' '}
              {formatPercent(VARIABLE_FIRM_DECLINE_SCHEDULE[1])}, and the
              third and every later unit receive the{' '}
              {formatPercent(
                VARIABLE_FIRM_DECLINE_SCHEDULE[
                  VARIABLE_FIRM_DECLINE_SCHEDULE.length - 1
                ]
              )} floor. This applies to duplicates and to different
              technologies in the same family. For example, Concentrated
              Solar has the higher starting firm capacity, so it receives
              full credit before Solar PV receives 75%. Annual generation
              still counts in full.
            </li>
            <li>
              <strong>Disruptions hurt the peak less than they hurt the
              year.</strong> A card that cuts a plant's output cuts its
              firm capacity by only half as much — one bad year does not
              make a plant unreliable at peak. A plant knocked fully
              offline is the exception. Fossil Fuel Price Shock cuts
              Fossil Thermal firm capacity by 10%, and Battery Safety
              Recall halves Grid Battery firm capacity, without reducing
              annual generation. Demand Spike raises annual and peak
              demand, then reduces Fossil Thermal firm capacity to 85%
              and Nuclear firm capacity to 75%.
            </li>
          </ul>
        </section>
      ) : null}

      {advanced ? (
        <section className="rules-section">
          <h3>Resource development premium</h3>
          <p data-testid="rules-resource-premium">
            The first {RESOURCE_DEVELOPMENT_PREMIUM_STARTS_AT_COPY - 1}{' '}
            player-built copies of each exact technology use the listed
            capital cost. Copy {RESOURCE_DEVELOPMENT_PREMIUM_STARTS_AT_COPY}{' '}
            and every later copy adds a one-time{' '}
            {formatMoney(RESOURCE_DEVELOPMENT_PREMIUM_PER_BUILD)} resource
            development premium when built.
          </p>
          <p>
            The count runs across all three years. Retiring a plant does not
            reduce it or refund a premium already paid. Inherited plants do
            not count because the player did not build them.
          </p>
        </section>
      ) : null}

      {advanced ? (
        <section className="rules-section">
          <h3>Storage</h3>
          <p>
            Storage is not a power plant. It generates nothing. Its only
            job is to help you cover the peak hour.
          </p>
          <ul className="rules-list">
            <li>
              <strong>It has to be charged, and charge comes from spare
              energy.</strong> A store fills from energy your plants make
              beyond what demand needs that year.{' '}
              <strong>
                No spare energy, no charge — and an empty store is worth
                nothing at the peak.
              </strong>
            </li>
            <li>
              <strong>Two ratings, two different jobs.</strong> The GW
              rating is how much it can deliver during the peak hour.
              The TWh rating is how much energy it holds. Filling it does
              not raise the GW rating — that ceiling is fixed by the
              technology. A bigger store is not a stronger one.
            </li>
            <li>
              <strong>Spare energy fills the most useful store first.</strong>{' '}
              If you own more than one, the game charges whichever
              returns the most firm capacity per TWh of spare energy,
              then the next. Buying another store can therefore never
              reduce the firm capacity you already had — but when spare
              energy is tight, the one at the back of the queue may get
              nothing. Several units of the same store act as one bigger
              store: they pool their charge, and every unit needs
              filling.
            </li>
            <li>
              <strong>Only {carrierName} keeps its charge.</strong> It
              carries charge into the next year and starts already
              part-charged, contributing at the peak without using that
              year's spare energy. It loses some in the handover — charge
              is worth {formatPercent(carrierEfficiency)} of itself a
              year later, and less again the year after — so hoarding
              decays. {nonCarrierNames} starts every year empty.
            </li>
          </ul>
          <table className="rules-table" data-testid="rules-storage-table">
            <thead>
              <tr>
                <th>Storage</th>
                {STORAGE_PLANTS.map((p) => (
                  <th key={p.id}>{p.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row">Firm capacity at peak</th>
                {STORAGE_PLANTS.map((p) => (
                  <td key={p.id}>
                    {formatGw(p.capacityUnits * p.firmCapacityCredit)}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Energy stored</th>
                {STORAGE_PLANTS.map((p) => (
                  <td key={p.id}>{formatTwh(p.storageEnergyUnits)}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Surplus to fill</th>
                {STORAGE_PLANTS.map((p) => (
                  <td key={p.id}>{formatTwh(storageSurplusToFill(p))}</td>
                ))}
              </tr>
              <tr>
                <th scope="row">Firm per TWh of surplus</th>
                {STORAGE_PLANTS.map((p) => (
                  <td key={p.id}>
                    {(
                      (p.capacityUnits * p.firmCapacityCredit) /
                      storageSurplusToFill(p)
                    ).toFixed(2)}
                  </td>
                ))}
              </tr>
              <tr>
                <th scope="row">Keeps charge next year</th>
                {STORAGE_PLANTS.map((p) => (
                  <td key={p.id}>
                    {p.carriesEnergyBetweenYears
                      ? `${formatPercent(p.roundTripEfficiency)} of it`
                      : 'No'}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
          {/*
            <strong>Removed storage note:</strong> compare the stores on{' '}
            <em>surplus to fill</em>, not on their round-trip
            percentages — the two round-trip figures are not comparable,
            because they measure different things. A store that cannot
            carry pays its loss on the way in, so it draws more spare
            energy than it holds. {carrierName} charges at face value and
            pays its loss only when charge crosses into the next year.
          */}
        </section>
      ) : null}

      {advanced ? (
        <section className="rules-section">
          <h3>Your starting grid</h3>
          <p>
            You do not start from nothing. Two ageing fossil plants are
            already connected, and you did not choose them. Their capital
            was spent long before you arrived, so they cost you nothing to
            build — but you pay their operating cost and their emissions
            every year you keep them, and they occupy{' '}
            {ADVANCED_LEGACY_PLANTS.length} of your{' '}
            {slotsNewPerYear[1]} Year 1 connection slots.
          </p>
          <table className="rules-table">
            <thead>
              <tr>
                <th>Plant</th>
                <th>Running at</th>
                <th>Output</th>
                <th>Firm</th>
                <th>Opex/yr</th>
                <th>Emissions/yr</th>
              </tr>
            </thead>
            <tbody>
              {ADVANCED_LEGACY_PLANTS.map((p) => (
                <tr key={p.id}>
                  <td>{p.name}</td>
                  <td>{Math.round(p.legacyDerateFactor * 100)}%</td>
                  <td>{p.output} TWh</td>
                  <td>
                    {(p.capacityUnits * p.firmCapacityCredit).toFixed(2)} GW
                  </td>
                  <td>{formatMoney(p.opex)}</td>
                  <td>+{p.emissions}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p>
            These plants are old and run below their nameplate rating, but
            they burn fuel as if they did not: the derate cuts their output
            and firm capacity, and leaves emissions and operating cost
            untouched. That makes them dirtier and costlier per TWh than a
            new plant of the same type would be. A plant you build yourself
            is new and runs at full specification.
          </p>
        </section>
      ) : null}

      {advanced ? (
        <section className="rules-section">
          <h3>Retiring plants</h3>
          <p>
            During any build phase — including Year 1 — you may retire
            plants you already own. Retirement happens before new builds
            resolve, so the plant contributes nothing that year: no energy,
            no firm capacity, no emissions, no operating cost.
          </p>
          <ul className="rules-list">
            <li>
              Each retirement costs a{' '}
              {formatMoney(DECOMMISSION_FEE_PER_PLANT)} decommissioning
              fee, paid in the year of retirement.
            </li>
            <li>
              Closing a plant <strong>early</strong> costs more on top:
              an early-closure charge of{' '}
              {EARLY_CLOSURE_SURCHARGE} x the plant&rsquo;s capital cost
              in Year 1, half that in Year 2, and nothing in Year 3.
              Breaking contracts at short notice is expensive, and a big
              plant costs more to unwind than a small one. Closing the
              inherited Coal Plant costs{' '}
              {formatMoney(decommissionFeeForPlant(coalLegacy, 1))} in
              Year 1 against{' '}
              {formatMoney(decommissionFeeForPlant(coalLegacy, 3))} in
              Year 3.
            </li>
            <li>
              Capital already spent is sunk and is never refunded, but the
              grid connection slot is freed: each retirement adds one
              build slot in the same build phase.
            </li>
            <li>
              Retiring is a real trade-off. Losing firm capacity in a
              tight year can cost far more than the emissions you save, so
              build replacement capacity before you exit.
            </li>
          </ul>
        </section>
      ) : null}

      <section className="rules-section">
        <h3>Carbon tax brackets</h3>
        <p>
          Total emissions across all three years are looked up in this
          table at the end of the game. Positive bracket values are
          bonuses; negative bracket values are penalties. The COP 31 Antalya outcome
          then multiplies the bracket value.
        </p>
        {advanced ? (
          <p data-testid="rules-carbon-weights">
            <strong>Escalating carbon price.</strong> Emissions are
            weighted by year before this table is applied:{' '}
            {[1, 2, 3]
              .map((y) => `Year ${y} x${ADVANCED_CARBON_YEAR_WEIGHTS[y]}`)
              .join(', ')}
            . Polluting early is cheaper than polluting late, so a fossil
            bridge you exit costs less than one you keep.
            Negative emissions receive credit only for post-disaster clean
            generation that can meet that year&rsquo;s demand. Surplus clean
            generation beyond demand does not create extra carbon credit.
          </p>
        ) : null}
        <table className="rules-table">
          <thead>
            <tr>
              <th>Total emissions</th>
              <th>Effect</th>
            </tr>
          </thead>
          <tbody>
            {CARBON_TAX_BRACKETS.map((b, i) => (
              <tr key={i}>
                <td>{b.label}</td>
                <td>{formatMoney(b.base, { sign: true })}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="rules-section">
        <h3>Disruptions and Risk Outlook</h3>
        <p>
          There are {disruptionCards.length} possible
          disruption events in the game. Each completed run reveals 6 of
          them: 1 in Year 1, 2 in Year 2, and 3 in Year 3. Disruptions can
          reduce output, raise fossil operating costs, increase demand, or
          change policy conditions.
          {advanced
            ? ' In Advanced Mode they can also cut firm capacity and raise peak demand.'
            : ''}
        </p>
        <p>
          A card can be drawn only once in a run. Year 1 uses an even draw
          from the full remaining deck. In Years 2 and 3, the Risk Outlook
          assigns Very High, High, Standard, Low or Very Low labels to the
          remaining cards before you build. These labels change the relative
          draw weight. They are warnings rather than guarantees.
        </p>
        <ul className="rules-list">
          <li>
            Effects from cards drawn in the same year stack. Output factors
            multiply, while demand additions and cost changes are applied to
            their own parts of the calculation.
          </li>
          <li>
            A plant-specific card has no effect if you own none of its targets.
            Supply Chain Bottleneck selects your largest non-nuclear family
            when it resolves. Grid Connection Constraint selects your largest
            family, including Nuclear. A tied largest family is chosen at
            random.
          </li>
          <li>
            Grid Connection Constraint remains for later years and also affects
            later plants in the selected family. Subsidy Reform permanently
            removes the fossil fuel opex discount.
          </li>
          {advanced ? (
            <li>
              Advanced resolves annual generation and firm capacity as separate
              channels. Most generation disruptions have a softer firm-capacity
              effect, while named exceptions use the exact firm reduction shown
              on their card. Demand Spike also raises peak demand and reduces
              thermal firm capacity for that year.
            </li>
          ) : null}
        </ul>
        <details className="rules-disruption-reference">
          <summary>
            View all {disruptionCards.length} disruption cards and effects
          </summary>
          <p className="rules-disruption-reference-intro">
            Card effects below show the complete schedule when an amount changes
            by year. During a run, the revealed card shows the current year and
            any mild or severe result that was rolled.
          </p>
          <ul className="rules-disaster-grid" role="list">
            {disruptionCards.map((disaster) => (
              <li key={disaster.id}>
                <DisasterCard disaster={disaster} advanced={advanced} />
              </li>
            ))}
          </ul>
        </details>
      </section>

      <section className="rules-section">
        <h3>COP 31 Antalya outcomes</h3>
        <ul className="rules-list">
          <li>
            <strong>Summit collapses.</strong> Weak climate action. Clean energy
            support is halved (0.5x), and weak enforcement softens carbon
            penalties to 0.5x.
          </li>
          <li>
            <strong>Modest agreement reached.</strong> Standard tax and bonus apply,
            with no changes.
          </li>
          <li>
            <strong>Historic ambitious targets adopted.</strong> Carbon tax and
            bonuses are multiplied by 1.5x.
          </li>
        </ul>
      </section>

      <section className="rules-section">
        <h3>{advanced ? 'Advanced scoring' : 'Post-Year-3 awards'}</h3>
        {advanced ? (
          <p data-testid="rules-advanced-no-awards">
            Advanced has no post-game Clean Energy Transition, System
            Resilience or Affordability awards. Your final total includes
            capital, operating costs, decommissioning, both shortfall
            layers, and the COP 31 Antalya adjusted carbon amount.
          </p>
        ) : (
          <p>
            At the end of the game, two flat awards are available and are
            subtracted from your total cost. They use post-disaster TWh
            across all three years and neither is multiplied by the COP 31
            outcome.
          </p>
        )}
        {!advanced ? (
          <>
        <h4 className="rules-award-heading rules-award-heading-clean">
          Clean Energy Transition Award
        </h4>
        <p>
          This award uses the share of post-disaster output from eligible clean
          technologies. Larger awards require a meaningful share from variable
          clean technologies.
        </p>
        <table className="rules-table">
          <thead>
            <tr>
              <th>Requirement</th>
              <th>Award</th>
            </tr>
          </thead>
          <tbody>
            {CLEAN_ENERGY_AWARD_TIERS.map((t) => (
              <tr key={`${t.minCleanShare}-${t.minVariableCleanShare}`}>
                <td>
                  <strong>{t.label}.</strong> {formatPercent(t.minCleanShare)}+ clean
                  {t.minVariableCleanShare > 0
                    ? `, ${formatPercent(t.minVariableCleanShare)}+ variable clean`
                    : ''}
                </td>
                <td>{formatMoney(t.bonus, { sign: true })}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <h4 className="rules-award-heading rules-award-heading-resilience">
          System Resilience Award
        </h4>
        <p>
          This award uses the number of distinct technology families built and
          the largest single family share of post-disaster TWh.
        </p>
        <table className="rules-table">
          <thead>
            <tr>
              <th>Requirement</th>
              <th>Award</th>
            </tr>
          </thead>
          <tbody>
            {RESILIENCE_AWARD_TIERS.map((t) => (
              <tr key={`${t.minFamilies}-${t.maxLargestFamilyShareExclusive}`}>
                <td>
                  <strong>{t.label}.</strong> {t.minFamilies}+ families, largest family below{' '}
                  {formatPercent(t.maxLargestFamilyShareExclusive)}
                  {t.requiresFossilFreePortfolio
                    ? ', no fossil thermal plants on the final grid'
                    : ''}
                </td>
                <td>{formatMoney(t.bonus, { sign: true })}</td>
              </tr>
            ))}
          </tbody>
        </table>
          </>
        ) : null}
      </section>

      <section className="rules-section">
        <h3>Key mechanics</h3>
        <ul className="rules-list">
          <li>
            <strong>Fossil subsidy.</strong> Active by default. Halves
            Fossil Thermal opex every year until the Subsidy Reform policy
            signal materialises, after which it is gone for the rest of the game.
          </li>
          <li>
            <strong>Economies of scale.</strong>{' '}
            {advanced && ADVANCED_EOS_FIRST_COPY_PAYS_FULL ? (
              <>
                The first copy of an exact technology pays full opex. Every
                later copy receives Duplicate EOS:{' '}
                <span data-testid="rules-eos-multiplier">
                  x{ADVANCED_EOS_DUPLICATE_MULTIPLIER}
                </span>{' '}
                opex.
              </>
            ) : (
              <>
                Building at least two of an exact technology gives each
                copy Duplicate EOS:{' '}
                <span data-testid="rules-eos-multiplier">
                  x{EOS_DUPLICATE_MULTIPLIER}
                </span>{' '}
                opex.
              </>
            )}{' '}
            The $0.5M is the opex floor.
          </li>
          <li>
            <strong>Variable output.</strong> Each variable plant type
            you own has one output outcome per year. Different types are
            assessed independently. Disruption output multipliers stack
            multiplicatively on the resolved value.
          </li>
          <li>
            <strong>{advanced ? 'Energy shortfall' : 'Shortfall'}.</strong> If
            your output is below demand, the first 5 TWh short cost $4M
            each, then $9M per TWh beyond that.
            {advanced
              ? ' The peak capacity shortfall penalty is charged separately, on top of this.'
              : ''}
          </li>
          <li>
            <strong>Risk Outlook.</strong> Provides a description and risk
            likelihood labels for the potential disruptions that can impact your
            plant portfolio.
          </li>
          <li>
            <strong>Family.</strong> Family refers to groups of power plants that
            share similarities, for example Run-of-River Hydro and Large Hydro
            Dam are in the same family. It is relevant for disruption
            vulnerability{advanced ? '' : ' and award eligibility'}. Total
            families available = 7
            {advanced ? ', plus Storage in this mode' : ''}.
          </li>
        </ul>
      </section>

      <section className="rules-section">
        <h3>Plan rating</h3>
        <p data-testid="rules-plan-rating">
          Disruptions are random, so a weak plan can get lucky and a strong
          plan can get mauled. At the end of the game your exact plan is
          replayed across {PLAN_RATING_HISTORIES} alternate histories, each
          with fresh disruptions, output rolls, and COP 31 outcome. The
          final score screen shows the expected cost of your plan and how
          lucky your actual run was compared with those histories.
        </p>
        <p>
          The expected cost measures your planning; the gap between it and
          your score is the dice. A tight range between the best and worst
          histories is itself a sign of a resilient portfolio.
        </p>
        <p>
          Every plan is replayed against the same set of histories, so the
          Best Plans board compares plans rather than luck: submit the same
          plan twice and it rates identically, and two players who build the
          same thing get the same number.
        </p>
      </section>
    </div>
  );
}
